import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";
import { Plus, X, AlertTriangle } from "lucide-react";
import { Switch } from "../../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";

export default function Appearance() {
  const [data, setData] = useState(null);
  const [faq, setFaq] = useState("");
  const [marquee, setMarquee] = useState("");

  useEffect(() => {
    api.get("/admin/appearance").then((r) => {
      setData(r.data);
      setFaq(JSON.stringify(r.data.faq || [], null, 2));
      setMarquee((r.data.marquee_words || []).join("\n"));
    });
  }, []);

  const save = async () => {
    let parsedFaq = data.faq;
    try { parsedFaq = JSON.parse(faq); } catch { toast.error("FAQ não é JSON válido"); return; }
    const words = marquee.split("\n").map((s) => s.trim()).filter(Boolean);
    await api.put("/admin/appearance", { ...data, faq: parsedFaq, marquee_words: words });
    toast.success("Aparência atualizada");
  };

  if (!data) return <p className="text-ozx-muted">Carregando...</p>;
  const set = (k, v) => setData({ ...data, [k]: v });
  const updateGallery = (idx, val) => {
    const arr = [...(data.gallery_images || [])]; arr[idx] = val; set("gallery_images", arr);
  };
  const removeGallery = (idx) => { const arr = [...data.gallery_images]; arr.splice(idx, 1); set("gallery_images", arr); };
  const addGallery = () => set("gallery_images", [...(data.gallery_images || []), ""]);

  return (
    <div className="max-w-4xl">
      <p className="text-xs uppercase tracking-[0.25em] text-ozx-primary mb-2">Configuração</p>
      <h1 className="font-display text-4xl font-medium tracking-tight mb-8">Aparência</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="glass-card rounded-3xl p-5">
          <ImageUpload label="Logo" value={data.logo_url} onChange={(v) => set("logo_url", v)} testId="appearance-logo" />
          <div className="mt-4">
            <Label className="text-ozx-muted text-xs uppercase tracking-wider mb-2 block">Tamanho do logo (altura em px)</Label>
            <Input type="number" min="20" max="80" value={data.logo_size || 32} onChange={(e) => set("logo_size", Number(e.target.value))} className="bg-white/5 border-white/10 text-white" data-testid="appearance-logo-size" />
            <p className="text-xs text-ozx-muted mt-1">Recomendado entre 28 e 60. Valor atual: {data.logo_size || 32}px</p>
          </div>
        </div>
        <div className="glass-card rounded-3xl p-5">
          <ImageUpload label="Imagem hero (fundo)" value={data.hero_image_url} onChange={(v) => set("hero_image_url", v)} testId="appearance-hero" />
        </div>
        <div className="glass-card rounded-3xl p-5 md:col-span-2">
          <ImageUpload label="Imagem ao lado do título (hero)" value={data.hero_side_image_url} onChange={(v) => set("hero_side_image_url", v)} testId="appearance-hero-side" />
          <p className="text-xs text-ozx-muted mt-2">Aparece ao lado de "Ozoxx Experience" no topo da landing. Ideal: imagem com fundo transparente (PNG).</p>
        </div>
        <div className="glass-card rounded-3xl p-5 md:col-span-2">
          <ImageUpload label="Logo secundária (acima do título)" value={data.hero_secondary_logo_url} onChange={(v) => set("hero_secondary_logo_url", v)} testId="appearance-hero-logo" />
          <p className="text-xs text-ozx-muted mt-2">Pequena logo/símbolo exibido acima de "Ozoxx Experience" no hero. Ideal: PNG transparente quadrado/horizontal.</p>
        </div>
      </div>

      <div className="glass-card rounded-3xl p-6 mb-6">
        <Label className="text-xs uppercase text-ozx-muted mb-3 block">Galeria de fotos</Label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(data.gallery_images || []).map((src, i) => (
            <div key={i} className="relative">
              <ImageUpload label="" value={src} onChange={(v) => updateGallery(i, v)} testId={`gallery-${i}`} />
              <button onClick={() => removeGallery(i)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-ozx-danger text-white flex items-center justify-center text-xs"><X className="w-3 h-3" /></button>
            </div>
          ))}
          <button onClick={addGallery} className="h-32 glass-card rounded-2xl border-dashed flex items-center justify-center text-ozx-muted hover:text-white"><Plus className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="glass-card rounded-3xl p-6 mb-6">
        <Label className="text-xs uppercase text-ozx-muted mb-4 block">Cards da seção "Sobre"</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label className="text-xs uppercase text-ozx-muted">Card 1 — Título</Label>
            <Input value={data.about_card1_title || ""} onChange={(e) => set("about_card1_title", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="about-card1-title-input" placeholder="Programação imersiva" />
            <Label className="text-xs uppercase text-ozx-muted">Card 1 — Texto</Label>
            <Textarea value={data.about_card1_text || ""} onChange={(e) => set("about_card1_text", e.target.value)} rows={4} className="bg-white/5 border-white/10 text-white" data-testid="about-card1-text-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase text-ozx-muted">Card 2 — Título</Label>
            <Input value={data.about_card2_title || ""} onChange={(e) => set("about_card2_title", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="about-card2-title-input" placeholder="Networking de alto nível" />
            <Label className="text-xs uppercase text-ozx-muted">Card 2 — Texto</Label>
            <Textarea value={data.about_card2_text || ""} onChange={(e) => set("about_card2_text", e.target.value)} rows={4} className="bg-white/5 border-white/10 text-white" data-testid="about-card2-text-input" />
          </div>
        </div>
        <p className="text-xs text-ozx-muted mt-3">Editáveis os dois cards informativos da seção "Sobre o evento" na landing page.</p>
      </div>

      <div className="space-y-5 mb-6">
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs uppercase text-ozx-muted mb-1.5 block">Primária</Label>
          <Input value={data.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="bg-white/5 border-white/10 text-white" /></div>
          <div><Label className="text-xs uppercase text-ozx-muted mb-1.5 block">Secundária</Label>
          <Input value={data.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} className="bg-white/5 border-white/10 text-white" /></div>
          <div><Label className="text-xs uppercase text-ozx-muted mb-1.5 block">Fundo</Label>
          <Input value={data.background_color} onChange={(e) => set("background_color", e.target.value)} className="bg-white/5 border-white/10 text-white" /></div>
        </div>
        <div>
          <Label className="text-xs uppercase text-ozx-muted mb-1.5 block">Frases do marquee rotativo (uma por linha)</Label>
          <Textarea value={marquee} onChange={(e) => setMarquee(e.target.value)} rows={6} className="bg-white/5 border-white/10 text-white font-mono text-xs" placeholder="OZOXX EXPERIENCE&#10;SÃO PAULO 2026&#10;08•09 OUTUBRO" data-testid="appearance-marquee" />
          <p className="text-xs text-ozx-muted mt-1">Frases que ficam rodando na faixa abaixo do hero. Use maiúsculas para mais impacto.</p>
        </div>
        <div>
          <Label className="text-xs uppercase text-ozx-muted mb-1.5 block">FAQ (JSON: [&#123;q,a&#125;])</Label>
          <Textarea value={faq} onChange={(e) => setFaq(e.target.value)} rows={8} className="bg-white/5 border-white/10 text-white font-mono text-xs" />
        </div>
      </div>

      <div className="glass-card rounded-3xl p-6 mb-6 border-yellow-500/20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <Label className="text-sm font-medium">Faixa de aviso (banner global)</Label>
          </div>
          <Switch checked={!!data.notice_enabled} onCheckedChange={(v) => set("notice_enabled", v)} data-testid="notice-enabled" />
        </div>
        <p className="text-xs text-ozx-muted mb-4">Quando ativo, aparece no topo de TODAS as páginas públicas do site. Use para avisos importantes (instabilidade, manutenção, último dia de promoção, etc).</p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
          <div>
            <Label className="text-xs uppercase text-ozx-muted mb-1.5 block">Texto do aviso</Label>
            <Textarea
              value={data.notice_text || ""}
              onChange={(e) => set("notice_text", e.target.value)}
              rows={2}
              placeholder="Ex: Estamos passando por instabilidade no servidor. Em breve retornaremos à normalidade."
              className="bg-white/5 border-white/10 text-white"
              data-testid="notice-text"
            />
          </div>
          <div>
            <Label className="text-xs uppercase text-ozx-muted mb-1.5 block">Cor</Label>
            <Select value={data.notice_color || "yellow"} onValueChange={(v) => set("notice_color", v)}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="notice-color"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-ozx-bg2 text-white border-white/10">
                <SelectItem value="yellow">Amarelo (aviso)</SelectItem>
                <SelectItem value="red">Vermelho (urgente)</SelectItem>
                <SelectItem value="blue">Azul (informativo)</SelectItem>
                <SelectItem value="green">Verde (sucesso)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Button onClick={save} className="bg-ozx-primary text-ozx-bg font-semibold rounded-full px-8 py-5" data-testid="appearance-save">Salvar</Button>
    </div>
  );
}
